import type { ReactNode } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { plural } from '../../shared/plural';
import type { LooseRecord } from './cells';
import {
  CompactContractorCell,
  CompactRouteCell,
  CompactVehicleCell,
  CONTRACTOR_WIDTH_WITH_RATE_TYPE,
  ROUTE_WIDTH_WITH_RATE_TYPE,
} from './listCells';
import {
  listVariantsOf,
  loadingWindowLines,
  pointsLine,
  rateParts,
  rateText,
  requirementsHint,
  requirementsLine,
  variantPlate,
  variantsShownInVehicleCell,
  vehicleTypeHint,
  vehicleTypeLines,
  type ListVariant,
} from './bankFtl';
import { originText } from './candidateRanks';
import { RowAssignButton } from './RowAssignDialog';

/**
 * Komórki listy banku według makiety FTL (pkt 9.3, przełącznik
 * `bankFtlColumns`, 15.10.2026). Treść z makiety idzie do istniejących
 * kolumn, nie do nowych — lista ma się mieścić przy 1366 px (decyzja
 * 08.10.2026, rachunek w `bankFtl.ts`). Szerokości jak w `listCells.tsx`.
 */

const CAPTION_WIDTH = { xs: 160, xl: 240 } as const;
/**
 * Warianty węziej niż auto (150 px): w widoku planisty („Gotowe”,
 * „Wstępnie planowane”) kolumna „Pojazd” była dotąd głównie „—”, więc każdy
 * piksel wariantu poszerza tabelę. Numer auta mieści się w 100 px, login
 * autora stoi w drugiej linii i ucina się wielokropkiem (pełny w dymku).
 * Liczba wariantów znika za to ze „Znaczników” — tam zwalnia się do ~80 px.
 *
 * 100 px, nie 120 (integracja Etapu 1, 15.10.2026): przy 120 lista z kolumną
 * „€/km” przewijała się w bok przy 1366 px o 5 px (e2e `checkListFits`,
 * kolumna „Pojazd” 128 px z odstępami); −20 px daje ok. 15 px zapasu.
 */
const VEHICLE_WIDTH = 100;
/**
 * Najszerszy wiersz „dodał: login” pod numerem auta (02.11.2026). Długi login (np. „planista.glowny.demo”,
 * prawdziwe „imie.nazwisko”) rozpychał kolumnę „Pojazd” do pełnych 100 px i lista Trade przewijała się w bok
 * przy 1024 px; reszta loginu w dymku wariantu.
 */
export const VARIANT_AUTHOR_MAX_WIDTH = 84;

/** „Załadunek”: okno od–do w dwóch liniach, nie sam dzień. */
export const LoadingWindowCell = ({ record }: { record: LooseRecord }) => {
  const { start, end } = loadingWindowLines(record);
  return (
    <Box sx={{ whiteSpace: 'nowrap' }}>
      <Typography variant="body2" component="div">
        {start}
      </Typography>
      {end ? (
        <Typography variant="caption" component="div" color="text.secondary">
          {end}
        </Typography>
      ) : null}
    </Box>
  );
};

/** Relacja, a pod nią liczba punktów, wymagany typ i wymagania. */
export const RouteWithRequirementsCell = ({ record }: { record: LooseRecord }) => {
  const line = requirementsLine(record);
  return (
    <Stack spacing={0.25}>
      <CompactRouteCell record={record} />
      {line ? (
        <Tooltip title={requirementsHint(record)} enterDelay={400}>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            component="div"
            sx={{ maxWidth: CAPTION_WIDTH }}
            data-testid="route-requirements"
          >
            {line}
          </Typography>
        </Tooltip>
      ) : null}
    </Stack>
  );
};

/** Dymek wariantu: auto, kto dodał, skąd, opiekun auta. */
export function variantHint(v: ListVariant): string {
  return [
    `Wariant: ${variantPlate(v)}.`,
    v.addedBy ? `Dodał użytkownik ${v.addedBy}.` : 'Bez zapisanego autora (np. z importu).',
    originText(v.origin) ? `Źródło: ${originText(v.origin)}.` : null,
    v.ftlCaretaker ? `Opiekun pojazdu: ${v.ftlCaretaker}.` : null,
  ]
    .filter((s): s is string => s !== null)
    .join(' ');
}

/**
 * „Pojazd”: auto z floty, przewoźnik albo numer z TMS — jak dotąd; zlecenie
 * bez nich, z aktywnymi wariantami, pokazuje warianty: numer auta i kto je
 * dodał (makieta: „pojazd albo warianty z autorami”), najwyżej trzy, reszta
 * liczbą. Pod nimi — dla „Obsadzanie pojazdem” — „Obsadź…”.
 */
export const VehicleOrVariantsCell = ({ record }: { record: LooseRecord }) => {
  const { variants, count } = listVariantsOf(record);
  if (!variantsShownInVehicleCell(record)) {
    return <CompactVehicleCell record={record} />;
  }
  const more = count - variants.length;
  return (
    <Stack spacing={0.25} sx={{ maxWidth: VEHICLE_WIDTH, alignItems: 'flex-start' }} data-testid="list-variants">
      {variants.map((v) => (
        <Tooltip key={v.id} title={variantHint(v)} enterDelay={400}>
          <Box sx={{ maxWidth: VEHICLE_WIDTH }}>
            <Typography variant="body2" noWrap component="div">
              {variantPlate(v)}
            </Typography>
            {/* Autor w drugiej linii (przegląd Etapu 1, 15.10.2026): w jednej linii z numerem
                auta przy 100 px zostawał z niego sam wielokropek. */}
            <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ maxWidth: VARIANT_AUTHOR_MAX_WIDTH }}>
              {v.addedBy ? `dodał: ${v.addedBy}` : 'bez autora'}
            </Typography>
          </Box>
        </Tooltip>
      ))}
      {more > 0 ? (
        <Typography variant="caption" color="text.secondary">
          {`i ${plural(more, 'kolejny wariant', 'kolejne warianty', 'kolejnych wariantów')}`}
        </Typography>
      ) : null}
      <RowAssignButton record={record} />
    </Stack>
  );
};

/** „Kontrahent”, a pod nim stawka zlecenia (makieta: kolumna „Stawka”). */
export const ContractorWithRateCell = ({ record }: { record: LooseRecord }) => {
  const rate = rateText(record.price, record.currency);
  return (
    <Stack spacing={0.25}>
      <CompactContractorCell record={record} />
      <Tooltip title={rate ? 'Stawka zlecenia z TMS' : 'Zlecenie nie ma stawki w TMS'} enterDelay={400}>
        <Typography variant="caption" color="text.secondary" noWrap component="div" data-testid="rate">
          {`stawka ${rate ?? '—'}`}
        </Typography>
      </Tooltip>
    </Stack>
  );
};

// ── Osobne kolumny „Stawka” i „Typ pojazdu” (S-24.10-13) ────────────────
// Odpowiedź z 21.10.2026 na pytanie 20: „osobne kolumny stawki i typu —
// tak”. Przełącznik `bankRateTypeColumns`, razem z `bankFtlColumns`. Obie
// komórki dwulinijkowe i wąskie — lista ma się mieścić przy 1366 px (pomiar:
// e2e `checkListFits`, `yarn workspace e2e layout-audit`).

/**
 * Szerokość kolumny „Typ pojazdu”: typ (JUMBO, MEGA, STANDARD) mieści się
 * w 72 px; dłuższe („K 13,2 LDM”, „wózek · ADR · fix”) ucina wielokropek,
 * pełny opis jest w dymku. Nagłówek łamie się na dwie linie
 * (`RATE_TYPE_HEADER_CLASS` w `UserOfferList.tsx`).
 */
const TYPE_WIDTH = 72;

/** Relacja, a pod nią tylko liczba punktów — typ ma własną kolumnę. */
export const RouteWithPointsCell = ({ record }: { record: LooseRecord }) => {
  const line = pointsLine(record);
  return (
    <Stack spacing={0.25}>
      <CompactRouteCell record={record} maxWidth={ROUTE_WIDTH_WITH_RATE_TYPE} />
      {line ? (
        <Typography variant="caption" color="text.secondary" noWrap component="div" data-testid="route-points">
          {line}
        </Typography>
      ) : null}
    </Stack>
  );
};

/** „Typ pojazdu”: wymagany typ, pod nim wymagania (wózek, ADR, fix). */
export const VehicleTypeCell = ({ record }: { record: LooseRecord }) => {
  const { type, requirements } = vehicleTypeLines(record);
  if (type === null && requirements === null) {
    return (
      <Tooltip title={vehicleTypeHint(record)} enterDelay={400}>
        <Typography variant="body2" color="text.secondary" data-testid="vehicle-type">
          —
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={vehicleTypeHint(record)} enterDelay={400}>
      <Box sx={{ maxWidth: TYPE_WIDTH }} data-testid="vehicle-type">
        <Typography variant="body2" noWrap component="div">
          {type ?? '—'}
        </Typography>
        {requirements ? (
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {requirements}
          </Typography>
        ) : null}
      </Box>
    </Tooltip>
  );
};

/** „Stawka”: kwota, pod nią waluta; brak stawki w TMS — „—”. */
export const RateCell = ({ record }: { record: LooseRecord }) => {
  const rate = rateParts(record.price, record.currency);
  return (
    <Tooltip title={rate ? 'Stawka zlecenia z TMS' : 'Zlecenie nie ma stawki w TMS'} enterDelay={400}>
      <Box sx={{ whiteSpace: 'nowrap', textAlign: 'right' }} data-testid="rate-column">
        <Typography variant="body2" component="div">
          {rate?.amount ?? '—'}
        </Typography>
        {rate?.currency ? (
          <Typography variant="caption" color="text.secondary" component="div">
            {rate.currency}
          </Typography>
        ) : null}
      </Box>
    </Tooltip>
  );
};

/** Kontrahent jedną linią, węziej — stawka ma własną kolumnę (S-24.10-13). */
export const ContractorNarrowCell = ({ record }: { record: LooseRecord }) => (
  <CompactContractorCell record={record} maxWidth={CONTRACTOR_WIDTH_WITH_RATE_TYPE} />
);

/**
 * Dwie komórki listy jedna pod drugą — wąskie okno (poniżej 1280 px,
 * 29.10.2026, decyzja Bartka: „1024 px — zrób tak, żeby było dobrze
 * i czytelnie”). Bank łączy wtedy drugorzędne kolumny w pary (TMS z typem
 * pojazdu, spółkę z VAT, stawkę z €/km) — ta sama treść i te same dymki,
 * bez przewijania tabeli w bok. `align="right"` — liczby, jak stawka.
 */
export const StackedCell = ({
  children,
  align = 'left',
  testId,
}: {
  children: ReactNode;
  align?: 'left' | 'right';
  testId?: string;
}) => (
  <Stack
    spacing={0.5}
    data-testid={testId}
    sx={{ alignItems: align === 'right' ? 'flex-end' : 'flex-start', textAlign: align }}
  >
    {children}
  </Stack>
);
